import * as React from "react";
import apiClient from "@/api-client";
import { Alert, Button, Stack, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useOAuthStatus } from "@/hooks/api/use-oauth";
import { toast } from "@/components/core/toaster";

type Operation = { bookingToken: string; status: string; start: string; end: string; url?: string; message: string };
const errorMessage = (error: any) => error?.response?.data?.message || "Unable to reach Google Calendar. Try again.";

/** Booking requests of a Google Calendar plugin. Connecting the account is `OAuthConnection`. */
export function GoogleCalendarOperations({ pluginId, enabled }: { pluginId: string; enabled: boolean }) {
	const client = useQueryClient();
	const base = `/plugins/${pluginId}/google-calendar`;
	const { data: oauth } = useOAuthStatus(pluginId);
	const operations = useQuery({
		queryKey: ["google-calendar", pluginId, "operations"],
		queryFn: async (): Promise<Operation[]> => {
			const response = await apiClient.get(`${base}/operations`);
			return response.data;
		},
	});
	const recover = useMutation({
		mutationFn: (id: string) => apiClient.post(`${base}/operations/${id}/recover`),
		onSuccess: () => client.invalidateQueries({ queryKey: ["google-calendar", pluginId] }),
		onError: (error) => toast.error(errorMessage(error)),
	});
	const canRecover = oauth?.status === "connected" && enabled;

	if (operations.isError) return <Alert severity="error">Unable to load recent booking requests.</Alert>;
	if (!operations.data?.length) return null;
	return (
		<Stack spacing={1}>
			<Typography variant="subtitle2">Recent booking requests</Typography>
			{operations.data.map((operation) => (
				<Stack key={operation.bookingToken} spacing={0.5} sx={{ borderTop: 1, borderColor: "divider", pt: 1 }}>
					<Typography variant="body2">
						{new Date(operation.start).toLocaleString()} · {operation.status.replaceAll("_", " ")}
					</Typography>
					<Typography variant="caption" color="text.secondary">
						{operation.message}
					</Typography>
					{operation.status !== "confirmed" && (
						<Button
							size="small"
							disabled={recover.isPending || !canRecover}
							onClick={() => recover.mutate(operation.bookingToken)}
						>
							Check Google confirmation
						</Button>
					)}
					{operation.url && (
						<Button size="small" component="a" href={operation.url} target="_blank" rel="noopener noreferrer">
							Open in Google Calendar
						</Button>
					)}
				</Stack>
			))}
		</Stack>
	);
}
